import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Context for scroll-aware footer shadow.
 * When a scroll container (e.g. DialogScrollBody) has more content below,
 * it sets `canScrollDown: true` so DialogFooter shows an upward shadow.
 */
const DialogScrollContext = React.createContext<{
  canScrollDown: boolean;
  setCanScrollDown: (v: boolean) => void;
}>({ canScrollDown: false, setCanScrollDown: () => {} });

function DialogScrollProvider({ children }: { children: React.ReactNode }) {
  const [canScrollDown, setCanScrollDown] = React.useState(false);
  const value = React.useMemo(() => ({ canScrollDown, setCanScrollDown }), [canScrollDown]);
  return <DialogScrollContext.Provider value={value}>{children}</DialogScrollContext.Provider>;
}

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    overlayClassName?: string;
  }
>(({ className, children, hideCloseButton, overlayClassName, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] overflow-hidden border bg-background p-5 shadow-lg duration-200 [&>*]:min-w-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg',
        className,
      )}
      {...props}
    >
      <DialogScrollProvider>{children}</DialogScrollProvider>
      {!hideCloseButton && (
        <DialogPrimitive.Close className="absolute right-4 top-4 z-20 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-muted-foreground ring-offset-background transition-colors hover:bg-muted-foreground/20 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  variant,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { variant?: 'muted' }) => (
  <div
    className={cn(
      'relative z-10 shrink-0 flex flex-col space-y-1.5 text-center sm:text-left pb-2 bg-background',
      variant === 'muted' && '-mx-5 -mt-5 px-5 pt-5 pb-5 mb-3 rounded-t-md bg-muted',
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, style, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { canScrollDown } = React.useContext(DialogScrollContext);
  return (
    <div
      className={cn(
        'relative z-10 shrink-0 -mx-5 -mb-5 px-5 py-4 rounded-b-md transition-shadow duration-200 flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center sm:gap-2',
        className,
      )}
      style={{
        boxShadow: canScrollDown
          ? '0 -2px 6px -1px color-mix(in srgb, var(--color-foreground) 8%, transparent)'
          : 'none',
        ...style,
      }}
      {...props}
    />
  );
};
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogScrollContext,
};
